/**
 * Harmonic Drift - Input Manager
 * Handles mouse and touch input
 */

class InputManager {
    constructor() {
        this.mouse = {
            x: 0,
            y: 0,
            isDown: false,
            draggedNode: null
        };
        
        this.draggedInstId = null;
        this.canvasRect = { left: 0, top: 0 };
        
        // Callbacks
        this.onCanvasDown = null;
        this.onCanvasRightClick = null;
        this.onMove = null;
        this.onUp = null;
    }

    /**
     * Initialize input handling
     * @param {HTMLCanvasElement} canvas - Canvas element
     */
    init(canvas) {
        this.canvas = canvas;
        this.updateCanvasRect();
        
        // Canvas events
        canvas.addEventListener('mousedown', (e) => this.handleDown(e));
        canvas.addEventListener('touchstart', (e) => this.handleDown(e), { passive: false });
        canvas.addEventListener('contextmenu', (e) => this.handleRightClick(e));
        
        // Window events for move/up
        window.addEventListener('mousemove', (e) => this.handleMove(e));
        window.addEventListener('touchmove', (e) => this.handleMove(e), { passive: false });
        window.addEventListener('mouseup', (e) => this.handleUp(e));
        window.addEventListener('touchend', (e) => this.handleUp(e));
        
        // Update rect on resize
        window.addEventListener('resize', () => this.updateCanvasRect());
    }

    /**
     * Update stored canvas bounding rect
     */
    updateCanvasRect() {
        if (this.canvas) {
            this.canvasRect = this.canvas.getBoundingClientRect();
        }
    }

    /**
     * Handle mousedown/touchstart on canvas
     * @param {Event} e - Input event
     */
    handleDown(e) {
        e.preventDefault();
        this.updateMouse(e);
        this.mouse.isDown = true;
        
        if (this.onCanvasDown) {
            this.onCanvasDown(this.mouse.x, this.mouse.y);
        }
    }

    /**
     * Handle mousemove/touchmove
     * @param {Event} e - Input event
     */
    handleMove(e) {
        this.updateMouse(e);
        
        if (this.onMove && this.mouse.isDown) {
            this.onMove(this.mouse.x, this.mouse.y);
        }
    }

    /**
     * Handle mouseup/touchend
     * @param {Event} e - Input event
     */
    handleUp(e) {
        this.mouse.isDown = false;
        
        if (this.onUp) {
            this.onUp(this.mouse.x, this.mouse.y);
        }
    }

    /**
     * Handle right-click/contextmenu on canvas
     * @param {Event} e - Input event
     */
    handleRightClick(e) {
        e.preventDefault();
        this.updateMouse(e);
        
        if (this.onCanvasRightClick) {
            this.onCanvasRightClick(this.mouse.x, this.mouse.y);
        }
    }

    /**
     * Update mouse position from event
     * @param {Event} e - Input event
     */
    updateMouse(e) {
        const t = e.touches ? e.touches[0] : e;
        if (!t) return;
        
        this.canvasRect = this.canvas.getBoundingClientRect();
        this.mouse.x = t.clientX - this.canvasRect.left;
        this.mouse.y = t.clientY - this.canvasRect.top;
    }

    /**
     * Get current screen coordinates (for UI checks)
     * @returns {Object} { x, y } screen coordinates
     */
    getScreenCoords() {
        return {
            x: this.mouse.x + this.canvasRect.left,
            y: this.mouse.y + this.canvasRect.top
        };
    }

    /**
     * Start dragging an instrument from the palette
     * @param {Event} e - Input event
     * @param {string} instId - Instrument ID
     */
    startDrag(e, instId) {
        e.preventDefault();
        this.draggedInstId = instId;
        this.mouse.isDown = true;
        this.updateMouse(e);
    }

    /**
     * Clear the dragged instrument
     */
    clearDrag() {
        this.draggedInstId = null;
    }

    /**
     * Get the currently dragged instrument ID
     * @returns {string|null} Instrument ID or null
     */
    getDraggedInstId() {
        return this.draggedInstId;
    }

    /**
     * Set the dragged node
     * @param {Node|null} node - Node being dragged
     */
    setDraggedNode(node) {
        this.mouse.draggedNode = node;
    }

    /**
     * Get the dragged node
     * @returns {Node|null} Dragged node or null
     */
    getDraggedNode() {
        return this.mouse.draggedNode;
    }

    /**
     * Get mouse state
     * @returns {Object} Mouse state
     */
    getMouse() {
        return { ...this.mouse };
    }

    /**
     * Check if mouse is down
     * @returns {boolean}
     */
    isMouseDown() {
        return this.mouse.isDown;
    }

    /**
     * Check if coordinates are within canvas bounds
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     * @param {number} width - Canvas width
     * @param {number} height - Canvas height
     * @returns {boolean}
     */
    isInBounds(x, y, width, height) {
        return x > 0 && x < width && y > 0 && y < height;
    }
}

// Export singleton instance
export const inputManager = new InputManager();
